/**
 * Custom hook for Personal Gap Analysis data and state management
 * Centralizes all state, data fetching, and event handlers
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import {
  getPersonalAssessment,
  startPersonalAssessment,
  retakePersonalAssessment,
  getPersonalAssessmentHistory,
  getSpecificPersonalAssessment,
  downloadPersonalAssessmentPDF,
  getPersonalAssessmentSummary
} from '../services/api';
import { supabase } from '../lib/supabase';
import { PERSONAL_CATEGORY_MAP, CATEGORY_ORDER } from '../utils/personalGapAnalysisConstants';
import { getGapZone } from '../utils/personalGapAnalysisUtils';
import { useBusinessContext } from '../contexts/BusinessContext';
import logger from '../utils/logger';

export const usePersonalGapAnalysisData = () => {
  // Business context for headers
  const { getBusinessHeaders } = useBusinessContext();

  // Refs
  const hasAnimatedRef = useRef(false);

  // State declarations
  const [personalAssessment, setPersonalAssessment] = useState(null);
  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [hoveredGapZone, setHoveredGapZone] = useState(null);
  const [selectedGapFilter, setSelectedGapFilter] = useState(null);
  const [showScoringGuide, setShowScoringGuide] = useState(false);
  const [isScoringGuideCollapsed, setIsScoringGuideCollapsed] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [assessmentHistory, setAssessmentHistory] = useState([]);
  const [viewingHistoricalAssessment, setViewingHistoricalAssessment] = useState(false);
  const [showFullQuestionText, setShowFullQuestionText] = useState(false);
  const [categoryAnimationKeys, setCategoryAnimationKeys] = useState({});
  const [overallProgressAnimationKey, setOverallProgressAnimationKey] = useState(0);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [activityTypeFilter, setActivityTypeFilter] = useState(null);
  const [personalImpactFilter, setPersonalImpactFilter] = useState(null);
  const [showActivityTypeHelp, setShowActivityTypeHelp] = useState(false);
  const [showImpactTypeHelp, setShowImpactTypeHelp] = useState(false);
  const [showScoringGuideHelp, setShowScoringGuideHelp] = useState(false);
  const [showCDTHelp, setShowCDTHelp] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [initialQuestionId, setInitialQuestionId] = useState(null);
  const [isChartHovered, setIsChartHovered] = useState(false);
  const [selectedAssessmentTypes, setSelectedAssessmentTypes] = useState(() => {
    const saved = localStorage.getItem('personalAssessmentTypes');
    return saved ? JSON.parse(saved) : ['personal financial'];
  });
  const [showStartModal, setShowStartModal] = useState(false);
  const [showCategoryHelp, setShowCategoryHelp] = useState(null);

  // Load assessment and questions on mount
  useEffect(() => {
    loadAssessment();
    loadAllQuestions();
  }, []);

  /**
   * Load personal assessment data
   */
  const loadAssessment = async () => {
    setLoading(true);
    try {
      const result = await getPersonalAssessment();

      if (result.success) {
        logger.debug('Personal assessment loaded', { assessmentId: result.data.assessment_id || result.data.id });
        const transformedData = {
          ...result.data,
          assessment_id: result.data.assessment_id || result.data.id
        };
        setPersonalAssessment(transformedData);

        // Trigger animations only on initial load
        if (!hasAnimatedRef.current) {
          setTimeout(() => {
            const initialKeys = {};
            Object.keys(transformedData.category_scores || {}).forEach(catKey => {
              initialKeys[catKey] = 1;
            });
            setCategoryAnimationKeys(initialKeys);
            setOverallProgressAnimationKey(prev => prev + 1);
            hasAnimatedRef.current = true;
          }, 50);
        }
      } else {
        const startResult = await startPersonalAssessment();
        if (startResult.success) {
          const newResult = await getPersonalAssessment();
          const transformedData = {
            ...newResult.data,
            assessment_id: newResult.data.assessment_id || newResult.data.id
          };
          setPersonalAssessment(transformedData);

          if (!hasAnimatedRef.current) {
            setTimeout(() => {
              const initialKeys = {};
              Object.keys(transformedData.category_scores || {}).forEach(catKey => {
                initialKeys[catKey] = 1;
              });
              setCategoryAnimationKeys(initialKeys);
              setOverallProgressAnimationKey(prev => prev + 1);
              hasAnimatedRef.current = true;
            }, 50);
          }
        }
      }
    } catch (error) {
      logger.error('Error loading personal assessment', error);
    }
    setLoading(false);
  };

  /**
   * Load all questions with optional assessment types filter
   */
  const loadAllQuestions = async (assessmentTypes = null) => {
    try {
      // When viewing the dashboard, always load ALL assessment types
      // The selectedAssessmentTypes should only filter when taking the assessment
      const types = assessmentTypes || ['personal financial', 'personal relational', 'personal spiritual'];
      // Map 'personal wellbeing' to 'personal relational' for backend compatibility
      const mappedTypes = types.map(t => t === 'personal wellbeing' ? 'personal relational' : t);
      const typesParam = mappedTypes.join(',');
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/personal-assessment/questions?assessment_types=${encodeURIComponent(typesParam)}`;

      // Use getBusinessHeaders() which includes X-Business-Id for viewers
      const headers = getBusinessHeaders();

      const response = await fetch(url, {
        headers: headers
      });

      if (response.ok) {
        const data = await response.json();
        const questions = data.questions || [];
        setAllQuestions(questions);
        console.log('✅ Personal Assessment Questions Loaded:', {
          totalQuestions: questions.length,
          sampleCategories: questions.slice(0, 5).map(q => q.category),
          assessmentTypes: data.assessment_types
        });
        logger.debug('Personal assessment questions loaded', { count: questions.length, types: data.assessment_types });
        return questions;
      } else {
        console.error('❌ Failed to load questions:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Error loading questions:', error);
      logger.error('Error loading personal assessment questions', error);
    }
    return [];
  };

  /**
   * Get answered questions from assessment
   */
  const getAnsweredQuestions = () => {
    if (!personalAssessment || !personalAssessment.responses) return [];
    return personalAssessment.responses;
  };

  /**
   * Get questions for a specific category
   */
  const getCategoryQuestions = (categoryKey) => {
    const actualCategory = PERSONAL_CATEGORY_MAP[categoryKey] || categoryKey;
    return allQuestions.filter(q => q.category === actualCategory);
  };

  /**
   * Get answer for a specific question
   */
  const getAnswer = (questionId) => {
    const answered = getAnsweredQuestions();
    return answered.find(a => a.question_id === questionId);
  };

  /**
   * Use fixed category order (Financial → Wellbeing → Spiritual)
   * Only include categories that exist in the questions
   */
  const categoryOrder = useMemo(() => {
    const availableCategories = new Set(allQuestions.map(q => q.category).filter(Boolean));
    return CATEGORY_ORDER.filter(cat => availableCategories.has(cat));
  }, [allQuestions]);

  /**
   * Handle assessment modal save
   */
  const handleModalSave = async () => {
    await loadAssessment();
  };

  /**
   * Handle question edit
   */
  const handleQuestionEdit = (question, answer) => {
    setSelectedQuestion({
      ...question,
      answer_value: answer?.answer_value,
      answer_text: answer?.answer_text,
      comments: answer?.comments,
      id: answer?.id
    });
  };

  /**
   * Handle question save
   */
  const handleQuestionSave = async () => {
    setSelectedQuestion(null);
    await loadAssessment();
  };

  /**
   * Handle start assessment
   */
  const handleStartAssessment = () => {
    setShowStartModal(true);
  };

  /**
   * Handle confirm start assessment
   */
  const handleConfirmStartAssessment = async () => {
    const freshQuestions = await loadAllQuestions(selectedAssessmentTypes);
    setShowStartModal(false);

    const answeredQuestionIds = new Set(getAnsweredQuestions().map(q => q.question_id));
    const firstUnanswered = freshQuestions.find(q => !answeredQuestionIds.has(q.question_id));

    if (firstUnanswered) {
      setInitialQuestionId(firstUnanswered.question_id);
    } else {
      setInitialQuestionId(null);
    }

    setShowAssessmentModal(true);
  };

  /**
   * Toggle assessment type selection
   */
  const toggleAssessmentType = (type) => {
    if (type === 'personal financial') {
      return; // Required type, can't be toggled off
    }

    setSelectedAssessmentTypes(prev => {
      const newTypes = prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type];

      localStorage.setItem('personalAssessmentTypes', JSON.stringify(newTypes));
      return newTypes;
    });
  };

  /**
   * Handle retake assessment
   */
  const handleRetakeAssessment = async () => {
    if (window.confirm('Are you sure you want to start a new assessment? This will create a new assessment while keeping your previous one in history.')) {
      try {
        const result = await retakePersonalAssessment();
        if (result.success) {
          setViewingHistoricalAssessment(false);
          await loadAssessment();
          setShowAssessmentModal(true);
        }
      } catch (error) {
        logger.error('Error retaking personal assessment', error);
      }
    }
  };

  /**
   * Handle view history
   */
  const handleViewHistory = async () => {
    try {
      const result = await getPersonalAssessmentHistory();
      if (result.success) {
        const history = result.data.history || [];
        const historyWithTotals = history.map(h => ({
          ...h,
          assessment_id: h.id,
          total_questions: allQuestions.length
        }));
        setAssessmentHistory(historyWithTotals);
        setShowHistoryModal(true);
      }
    } catch (error) {
      logger.error('Error loading personal assessment history', error);
    }
  };

  /**
   * Handle load historical assessment
   */
  const handleLoadHistoricalAssessment = async (assessmentId) => {
    try {
      const result = await getSpecificPersonalAssessment(assessmentId);
      if (result.success) {
        const transformedData = {
          ...result.data,
          assessment_id: result.data.id,
          category_scores: {
            advisor_relationships: result.data.advisor_relationships_score,
            biblical_wisdom: result.data.biblical_wisdom_score,
            business_stewardship: result.data.business_stewardship_score,
            character_integrity: result.data.character_integrity_score,
            emotional_mental: result.data.emotional_mental_score,
            estate_planning: result.data.estate_planning_score,
            eternal_impact: result.data.eternal_impact_score,
            exit_vision: result.data.exit_vision_score,
            family_communication: result.data.family_communication_score,
            family_relationships: result.data.family_relationships_score,
            hobbies_interests: result.data.hobbies_interests_score,
            insurance_risk: result.data.insurance_risk_score,
            legacy_planning: result.data.legacy_planning_score,
            life_transition: result.data.life_transition_score,
            lifestyle_resource: result.data.lifestyle_resource_score,
            liquidity_cash: result.data.liquidity_cash_score,
            marriage_family: result.data.marriage_family_score,
            personal_financial: result.data.personal_financial_score,
            personal_spiritual: result.data.personal_spiritual_score,
            physical_health: result.data.physical_health_score,
            purpose_identity: result.data.purpose_identity_score,
            relational_health: result.data.relational_health_score,
            social_connection: result.data.social_connection_score,
            tax_planning: result.data.tax_planning_score,
            time_management: result.data.time_management_score,
            wealth_management: result.data.wealth_management_score
          }
        };
        setPersonalAssessment(transformedData);
        setViewingHistoricalAssessment(true);
        setShowHistoryModal(false);

        setTimeout(() => {
          const initialKeys = {};
          Object.keys(transformedData.category_scores || {}).forEach(catKey => {
            initialKeys[catKey] = 1;
          });
          setCategoryAnimationKeys(initialKeys);
          setOverallProgressAnimationKey(prev => prev + 1);
        }, 50);
      }
    } catch (error) {
      logger.error('Error loading historical personal assessment', error);
    }
  };

  /**
   * Handle back to current assessment
   */
  const handleBackToCurrent = async () => {
    setViewingHistoricalAssessment(false);
    await loadAssessment();
  };

  /**
   * Handle download PDF
   */
  const handleDownloadPDF = async () => {
    if (!personalAssessment || !personalAssessment.id) {
      alert('No assessment available to download');
      return;
    }

    // Get business ID from localStorage (same pattern as Business Gap Assessment)
    const businessId = localStorage.getItem('currentBusinessId');
    if (!businessId) {
      alert('Business ID not found. Please select a business first.');
      return;
    }

    setDownloadingPDF(true);
    try {
      const result = await downloadPersonalAssessmentPDF(businessId);
      if (!result.success) {
        alert('Failed to download PDF report: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      logger.error('Error downloading personal assessment PDF', error);
      alert('Failed to download PDF report');
    } finally {
      setDownloadingPDF(false);
    }
  };

  /**
   * Handle view summary
   */
  const handleViewSummary = async () => {
    if (!personalAssessment || !personalAssessment.id) {
      alert('No assessment available to summarize');
      return;
    }

    setLoadingSummary(true);
    setShowSummaryModal(true);
    try {
      const result = await getPersonalAssessmentSummary(personalAssessment.id);
      if (result.success) {
        setSummaryData(result.data);
      } else {
        alert('Failed to load assessment summary: ' + (result.error || 'Unknown error'));
        setShowSummaryModal(false);
      }
    } catch (error) {
      logger.error('Error loading personal assessment summary', error);
      alert('Failed to load assessment summary');
      setShowSummaryModal(false);
    } finally {
      setLoadingSummary(false);
    }
  };

  /**
   * Handle gap zone click for filtering
   */
  const handleGapZoneClick = (zone) => {
    const newFilter = selectedGapFilter === zone ? null : zone;
    setSelectedGapFilter(newFilter);

    if (newFilter) {
      const answered = getAnsweredQuestions();
      const categoriesToExpand = [];

      // Use categoryOrder from the hook instead of requiring it as a parameter
      categoryOrder.forEach(catKey => {
        const categoryQuestions = getCategoryQuestions(catKey);
        const hasMatchingQuestions = categoryQuestions.some(q => {
          const answer = answered.find(a => a.question_id === q.question_id);
          return answer && getGapZone(answer.score) === zone;
        });

        if (hasMatchingQuestions) {
          categoriesToExpand.push(catKey);
        }
      });

      setExpandedCategory(categoriesToExpand.length > 0 ? categoriesToExpand : []);
    } else {
      setExpandedCategory([]);
    }
  };

  /**
   * Toggle category expansion
   */
  const toggleCategory = (categoryKey) => {
    setExpandedCategory(prev => {
      if (prev.includes(categoryKey)) {
        return prev.filter(key => key !== categoryKey);
      } else {
        return [...prev, categoryKey];
      }
    });

    setTimeout(() => {
      setCategoryAnimationKeys(prev => ({
        ...prev,
        [categoryKey]: (prev[categoryKey] || 0) + 1
      }));
    }, 100);
  };

  return {
    // State
    personalAssessment,
    allQuestions,
    loading,
    showAssessmentModal,
    expandedCategory,
    selectedQuestion,
    hoveredGapZone,
    selectedGapFilter,
    showScoringGuide,
    isScoringGuideCollapsed,
    showHistoryModal,
    assessmentHistory,
    viewingHistoricalAssessment,
    showFullQuestionText,
    categoryAnimationKeys,
    overallProgressAnimationKey,
    downloadingPDF,
    activityTypeFilter,
    personalImpactFilter,
    showActivityTypeHelp,
    showImpactTypeHelp,
    showScoringGuideHelp,
    showCDTHelp,
    showSummaryModal,
    summaryData,
    loadingSummary,
    initialQuestionId,
    isChartHovered,
    selectedAssessmentTypes,
    showStartModal,
    showCategoryHelp,
    categoryOrder,

    // Setters
    setShowAssessmentModal,
    setExpandedCategory,
    setSelectedQuestion,
    setHoveredGapZone,
    setSelectedGapFilter,
    setShowScoringGuide,
    setIsScoringGuideCollapsed,
    setShowHistoryModal,
    setViewingHistoricalAssessment,
    setShowFullQuestionText,
    setCategoryAnimationKeys,
    setActivityTypeFilter,
    setPersonalImpactFilter,
    setShowActivityTypeHelp,
    setShowImpactTypeHelp,
    setShowScoringGuideHelp,
    setShowCDTHelp,
    setShowSummaryModal,
    setInitialQuestionId,
    setIsChartHovered,
    setShowStartModal,
    setShowCategoryHelp,

    // Functions
    loadAssessment,
    loadAllQuestions,
    getAnsweredQuestions,
    getCategoryQuestions,
    getAnswer,
    handleModalSave,
    handleQuestionEdit,
    handleQuestionSave,
    handleStartAssessment,
    handleConfirmStartAssessment,
    toggleAssessmentType,
    handleRetakeAssessment,
    handleViewHistory,
    handleLoadHistoricalAssessment,
    handleBackToCurrent,
    handleDownloadPDF,
    handleViewSummary,
    handleGapZoneClick,
    toggleCategory
  };
};
